<!-- eslint-disable max-len -->
<template>
  <div class="d-flex justify-content-center">
    <div class="general-wrap">
      <!-- Button Update -->
      <div class="d-flex justify-content-end">
        <button
          class="button-blue button-update"
          @click="updateApp"
        >
          Update
        </button>
      </div>

      <!-- If loading -->
      <skeleton-general-tab v-if="isLoading" />

      <!-- If not loading -->
      <div
        v-else
        class="card-content card-general"
      >
        <div>
          <!-- Icon -->
          <div class="config-section d-flex mb-10px">
            <div
              :class="{'error-icon' : $v.application.icon_url.$error}"
              class="preview-icon">
              <img
                v-if="application.icon_url"
                :src="application.icon_url"
              >
              <input
                ref="iconUpload"
                type="file"
                class="upload-photo"
                @change="handleIconUpload($event)"
              >
            </div>
            <div>
              <p class="title-config">Application Icon</p>
              <p class="desc-config">
                Icon must be minimum size 1024 x 1024px and .png format.
              </p>
              <p
                class="upload-btn"
                @click="$refs.iconUpload.click()"
              >
                Upload
              </p>
            </div>
          </div>

          <!-- General -->
          <div class="config-section">
            <div class="mb-2">
              <p class="title-config">General Information</p>
              <p class="desc-config">
                Information to describe the application
              </p>
            </div>

            <b-form-group
              :class="{'error-input' : $v.application.app_name.$error}"
              class="mb-1"
            >
              <label> Application Name </label>
              <j-input
                v-model="application.app_name"
                :error-required="false"
                placeholder="Enter application name"
              />
            </b-form-group>
            <b-form-group
              v-if="typeMode === 'hyperlink'"
              :class="{'error-input' : $v.application.app_url.$error}"
              class="mb-1"
            >
              <label> URL </label>
              <j-input
                v-model="application.app_url"
                :error-required="false"
                placeholder="Enter url"
              />
            </b-form-group>
            <b-form-group
              :class="{'error-input' : $v.application.desc_name.$error}"
              class="mb-1"
            >
              <label> Description </label>
              <j-textarea
                v-model="application.desc_name"
                :error-required="false"
                placeholder="Enter application description"
              />
            </b-form-group>
            <b-form-group>
              <label> Group Application </label>
              <j-single-select
                v-model="selectedGroup"
                :options="groupOptions"
                :error-required="false"
                placeholder="Select Group"
                value-format="object"
              />
            </b-form-group>
            <div
              v-if="typeMode === 'hyperlink'"
              class="d-flex justify-content-between align-items-center mt-3"
            >
              <label> Open Hyperlink in App Browser (Mobile) </label>
              <j-switch v-model="application.open_hyperlink_in_app_browser"/>
            </div>
            <div
              v-if="typeMode === 'hyperlink'"
              class="d-flex justify-content-between align-items-center mt-3"
            >
              <label> Use Token </label>
              <j-switch v-model="application.use_token"/>
            </div>
            <div
              v-if="application.is_offline_mode"
              class="local-storage mt-3"
            >
              <img
                src="@/assets/logo/offline-mode.png"
                height="16"
              >
              Local Storage Support
            </div>
          </div>
        </div>

        <div>
          <!-- Search Menu -->
          <div class="config-section d-flex justify-content-between align-items-center mb-10px">
            <div>
              <p class="title-config">Search Menu</p>
              <p class="desc-config">
                A search bar or search box in order to look up menu or page
              </p>
            </div>
            <j-switch v-model="application.search_menu" />
          </div>

          <!-- Display Mode -->
          <div class="config-section">
            <div class="mb-2">
              <p class="title-config">Display Mode</p>
              <p class="desc-config">
                Select the device that will be used to display this application
              </p>
            </div>

            <div class="d-flex align-items-center">
              <div
                v-for="option in listDisplayOptions"
                :key="option.id"
                :class="{ 'active': displayModeSelected.includes(option.id)}"
                class="btn-option"
                @click="handleDisplayMode(option.id)"
              >
                <div
                  v-if="!displayModeSelected.includes(option.id)"
                  class="circle-checkbox"
                />
                <img
                  v-if="displayModeSelected.includes(option.id)"
                  class="checked"
                  src="@/assets/new-form-ui/icon-active/check-circle-active.svg"
                >
                <img
                  :src="require(`@/assets/form-ui/${option.id}${displayModeSelected.includes(option.id) ? '-active' : ''}.svg`)"
                  class="icon-btn"
                >
                <span>{{ option.text }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable max-len, consistent-return, no-nested-ternary */
import { mapState, mapActions } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
// Mixins
import jMixUploadFile from '@/mixins/jMixUploadFile';
// Components
import jSwitch from '@/components/inputs/jSwitch';
import SkeletonGeneralTab from '@/views/application-manager/detail/app-tabs/partials/loading/skeletonGeneralTab';

export default {
  validations() {
    return {
      application: {
        app_name: { required },
        desc_name: { required },
        app_url: { required: requiredIf(() => this.typeMode === 'hyperlink') },
        is_offline_mode: {},
        open_hyperlink_in_app_browser: {},
        use_token: {},
        icon_url: { required },
        icon_launcher_url: { },
      },
    };
  },

  components: {
    jSwitch,
    SkeletonGeneralTab,
  },

  mixins: [jMixUploadFile],

  data() {
    return {
      isLoading: true,
      typeMode: '',
      groupOptions: [],
      selectedGroup: null,
      application: {
        app_id: null,
        app_name: null,
        desc_name: null,
        app_url: null,
        is_offline_mode: false,
        open_hyperlink_in_app_browser: false,
        use_token: false,
        search_menu: false,
        show_option: 3,
        icon_url: null,
        icon_launcher_url: null,
      },
      listDisplayOptions: [
        {
          id: 'web',
          text: 'Website',
        },
        {
          id: 'mobile',
          text: 'Mobile',
        },
      ],
      displayModeSelected: ['web', 'mobile'],
    };
  },

  computed: {
    ...mapState('jStoreSetting', [
      'application_detail', 'group_applications_list',
      'group_relations_list',
    ]),
  },

  watch: {
    application_detail(val) {
      this.setDataAppDetail(val);
    },
    displayModeSelected(val) {
      if (val.length === 0) this.application.show_option = 0;
      else if (val.length === 1 && val.includes('web')) this.application.show_option = 1;
      else if (val.length === 1 && val.includes('mobile')) this.application.show_option = 2;
      else if (val.length > 1) this.application.show_option = 3;
    },
  },

  created() {
    setTimeout(() => {
      this.setDataAppDetail(this.application_detail);
    }, 200);
    this.getGroupApplicationList().then(() => {
      this.isLoading = false;
    });
  },

  methods: {
    ...mapActions('jStoreSetting', [
      'actionUpdateApplication',
      'actionDeleteApplication',
      'actionGetGroupApplicationsList',
      'actionGetGroupApplicationsDetail',
      'actionGetGroupRelationsList',
    ]),

    handleDisplayMode(type) {
      if (this.displayModeSelected.includes(type)) {
        this.displayModeSelected = this.displayModeSelected.filter(f => f !== type);
      } else {
        this.displayModeSelected.push(type);
      }
    },

    async getGroupApplicationList() {
      const param = {
        params: {
          page: 1,
          limit: 0,
          column: 'id',
          sort: 'asc',
          query: '',
          query_type: '',
        },
      };
      const res = await this.actionGetGroupApplicationsDetail(param);
      if (res) {
        this.groupOptions = [];
        res.forEach((lab) => {
          if (lab.id !== 0) {
            this.groupOptions.push({
              text: lab.name,
              id: lab.id,
              is_delete: false,
            });
          }
        });

        const paramGetGroupRelationList = {
          params: {
            page: 1,
            limit: 0,
            column: 'id',
            sort: 'asc',
            query: '',
            query_type: '',
          },
        };
        const resp = await this.actionGetGroupRelationsList(paramGetGroupRelationList);
        if (resp) {
          this.setDataGroupRelationList(resp, res);
          return res;
        }
      }
    },

    setDataAppDetail(data) {
      if (data) {
        this.application.app_name = data.name;
        this.application.desc_name = data.description;
        this.application.app_url = data.hyperlink;
        this.application.is_offline_mode = data.is_offline_mode || false;
        this.application.open_hyperlink_in_app_browser = data.is_open_hyperlink_in_app_browser || false;
        this.application.search_menu = data.is_show_search_menu || false;
        this.application.use_token = data.type_platform === 5;
        this.application.show_option = data.show_option;
        this.application.icon_url = data.icon;
        this.application.icon_launcher_url = data.icon_launcher;
        this.application.app_id = data.id;
        this.typeMode = data.type_platform === 1 ? 'native' : 'hyperlink';

        if (this.application.show_option === 0) this.displayModeSelected = [];
        else if (this.application.show_option === 1) this.displayModeSelected = ['web'];
        else if (this.application.show_option === 2) this.displayModeSelected = ['mobile'];
        else if (this.application.show_option === 3) this.displayModeSelected = ['web', 'mobile'];
      }
    },

    setDataGroupRelationList(data, groupList) {
      const filtered = data.find(relation => relation.application_id === this.application.app_id);
      if (filtered) {
        const res = groupList.find(group => group.id === Number(filtered.group_application_id));
        this.selectedGroup = res || null;
      }
    },

    handleIconUpload(e) {
      this.handleFileUpload(e, ['image/png'], 5).then((res) => {
        this.application.icon_url = res;
        this.application.icon_launcher_url = res;
      });
    },

    async updateApp() {
      this.$v.application.$touch();
      if (!this.$v.application.$error) {
        const applicationId = this.application.app_id;
        const filtered = this.group_relations_list.find(relation =>
          relation.application_id === applicationId);

        const param = {
          name: this.application.app_name,
          icon: this.application.icon_url,
          icon_launcher: this.application.icon_launcher_url,
          description: this.application.desc_nameame,
          is_offline_mode: this.application.is_offline_mode,
          group_application_relation_id: filtered ? filtered.id : undefined,
          group_application_id: this.selectedGroup ? this.selectedGroup.id : undefined,
          type_platform: this.typeMode === 'hyperlink' ? (this.application.use_token ? 5 : 4) : 1,
          hyperlink: this.typeMode === 'hyperlink' ? this.application.app_url : '',
          is_open_hyperlink_in_app_browser: this.application.open_hyperlink_in_app_browser,
          is_show_search_menu: this.application.search_menu,
          show_option: this.application.show_option,
        };

        const res = await this.actionUpdateApplication({ applicationId, param });
        if (res.error) {
          this.$store.dispatch('jStoreNotificationScreen/toggleProblem', res.message);
        } else {
          this.getGroupApplicationList();
        }
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/application-manager/detail/app-tabs/jGeneralTab.vue